import { type ClewDatabase, dropDatabase, getDatabase } from './database';
import { startProcessesReplication } from './replication/processes';
import { startWorkspacesReplication } from './replication/workspaces';
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { type RxReplicationState } from 'rxdb/plugins/replication';

type DatabaseContextValue = {
  db: ClewDatabase;
  reSyncProcesses: () => void;
  userId: string;
};

const DatabaseContext = createContext<DatabaseContextValue | null>(null);

export const useDatabase = (): DatabaseContextValue => {
  const ctx = useContext(DatabaseContext);
  if (!ctx) {
    throw new Error('useDatabase used outside RxDBProvider');
  }

  return ctx;
};

type Props = {
  readonly children: ReactNode;
  readonly userId: string;
};

export const RxDBProvider = ({ children, userId }: Props) => {
  const [db, setDb] = useState<ClewDatabase | null>(null);
  const [activeProcessesReplication, setActiveProcessesReplication] =
    useState<null | RxReplicationState<unknown, unknown>>(null);

  const reSyncProcesses = useCallback(() => {
    activeProcessesReplication?.reSync();
  }, [activeProcessesReplication]);

  useEffect(() => {
    const replications: Array<RxReplicationState<unknown, unknown>> = [];
    let cancelled = false;

    (async () => {
      const instance = await getDatabase(userId);
      if (cancelled) {
        return;
      }

      const workspacesReplication = startWorkspacesReplication(
        instance,
        userId,
      ) as RxReplicationState<unknown, unknown>;
      const processesReplication = startProcessesReplication(
        instance,
        userId,
      ) as RxReplicationState<unknown, unknown>;

      setActiveProcessesReplication(processesReplication);
      replications.push(workspacesReplication, processesReplication);
      setDb(instance);
    })();

    const onFocus = () => {
      for (const replication of replications) {
        void replication.reSync();
      }
    };

    const onOnline = () => {
      for (const replication of replications) {
        void replication.reSync();
      }
    };

    document.addEventListener('visibilitychange', onFocus);
    window.addEventListener('online', onOnline);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onFocus);
      window.removeEventListener('online', onOnline);

      for (const replication of replications) {
        void replication.cancel();
      }
    };
  }, [userId]);

  const value = useMemo(
    () => (db ? { db, reSyncProcesses, userId } : null),
    [db, reSyncProcesses, userId],
  );

  if (!value) {
    return null;
  }

  return (
    <DatabaseContext.Provider value={value}>
      {children}
    </DatabaseContext.Provider>
  );
};

export const wipeLocalDatabase = () => dropDatabase();
